import * as React from 'react';
import { DateTime } from 'luxon';
import { ReferenceChildrenProps } from 'react-popper';
import dataAtIds from '../dataAtIds.json';
import { useMediaQuery, useSyncedStateAndProps } from '../hooks';
import Button from '../Button/Button';
import Buttons from '../Button/Buttons';
import DatePickerMonth from './DatePickerMonth';
import { Props as FlexProps } from '../Flex/Flex';
import Placement from '../Placement/Placement';
import PlacementArea from '../Placement/PlacementArea';
import PlacementManager from '../Placement/PlacementManager';
import PlacementReference from '../Placement/PlacementReference';
import './DatePicker.css';

const { useState } = React;

export interface Props extends FlexProps {
  /**
   * PlacementReference children prop for passing down
   * the ref of the target to be placed around.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: (props: ReferenceChildrenProps) => React.ReactNode;
  /**
   * The earliest date that can be selected.
   * Should be given in millisecond form. For example 1558973847330
   */
  dateSelectableFrom?: number;
  /**
   * The latest date that can be selected.
   * Should be given in millisecond form. For example 1558973847330
   */
  dateSelectableTo?: number;
  /**
   * The start date of the selected period.
   * Should be given in millisecond form. For example 1558973847330
   */
  dateSelectedFrom?: number;
  /**
   * The end date of the selected period.
   * Should be given in millisecond form. For example 1558973847330
   */
  dateSelectedTo?: number;
  /** Handler for when the selected period is chosen. */
  onApply?: (dateSelectedFrom?: number, dateSelectedTo?: number) => void;
  /** Handler for when the date picker has been asked to be closed.  */
  onClose: () => void;
  /** Flag for when the date picker should be visible. */
  visible: boolean;
}

/**
 * A simple date selection component, that supports a dual month view and
 * range selection.
 */
const DatePicker: React.FunctionComponent<Props> = (props: Props) => {
  const {
    children,
    onApply,
    onClose,
    visible,
  } = props;

  const match = useMediaQuery(['56rem']);
  const [dateSelectedFromMillis, setDateSelectedFromMillis] = useSyncedStateAndProps(props.dateSelectedFrom);
  const [dateSelectedToMillis, setDateSelectedToMillis] = useSyncedStateAndProps(props.dateSelectedTo);
  const [viewDateStartMillis, setViewDateStartMillis] = useState(dateSelectedFromMillis);
  const [viewDateEndMillis, setViewDateEndMillis] = useState(dateSelectedToMillis);

  const dateSelectableFrom = props.dateSelectableFrom === undefined ? undefined : DateTime.fromMillis(props.dateSelectableFrom).startOf('day');
  const dateSelectableTo = props.dateSelectableTo === undefined ? undefined : DateTime.fromMillis(props.dateSelectableTo);
  const dateSelectedFrom = dateSelectedFromMillis && DateTime.fromMillis(dateSelectedFromMillis).startOf('day');
  const dateSelectedTo = dateSelectedToMillis && DateTime.fromMillis(dateSelectedToMillis).startOf('day');

  const viewDateStart = viewDateStartMillis
    ? DateTime.fromMillis(viewDateStartMillis).startOf('month')
    : DateTime.local().startOf('month');

  const viewDateEnd = viewDateEndMillis && !DateTime.fromMillis(viewDateEndMillis).startOf('month').equals(viewDateStart)
    ? DateTime.fromMillis(viewDateEndMillis).startOf('month')
    : viewDateStart.plus({ months: 1 }).startOf('month');

  const handleClose = () => {
    onClose();
    setDateSelectedFromMillis(undefined);
    setDateSelectedToMillis(undefined);
  };

  const handleApply = () => {
    if (onApply) {
      onApply(dateSelectedFromMillis, dateSelectedToMillis);
    }

    handleClose();
  };

  const handlePreviousMonthStart = () => {
    setViewDateStartMillis(+viewDateStart.minus({ months: 1 }));
  };

  const handleNextMonthStart = () => {
    setViewDateStartMillis(+viewDateStart.plus({ months: 1 }));

    if (viewDateStart.plus({ months: 1 }).equals(viewDateEnd)) {
      setViewDateEndMillis(+viewDateEnd.plus({ months: 1 }));
    }
  };

  const handlePreviousMonthEnd = () => {
    setViewDateEndMillis(+viewDateEnd.minus({ months: 1 }));

    if (viewDateEnd.minus({ months: 1 }).equals(viewDateStart)) {
      setViewDateStartMillis(+viewDateStart.minus({ months: 1 }));
    }
  };

  const handleNextMonthEnd = () => {
    setViewDateEndMillis(+viewDateEnd.plus({ months: 1 }));
  };

  const handleSelect = (date: DateTime) => {
    let nextDateSelectedFrom: undefined | DateTime = dateSelectedFrom;
    let nextDateSelectedTo: undefined | DateTime = dateSelectedTo;

    if (dateSelectedFrom && dateSelectedTo) {
      nextDateSelectedFrom = date;
      nextDateSelectedTo = undefined;
    } else if (dateSelectedFrom) {
      nextDateSelectedTo = date;
    } else {
      nextDateSelectedFrom = date;
    }

    if (nextDateSelectedFrom && nextDateSelectedTo && +nextDateSelectedFrom > +nextDateSelectedTo) {
      [nextDateSelectedFrom, nextDateSelectedTo] = [nextDateSelectedTo, nextDateSelectedFrom];
    }

    setDateSelectedFromMillis(nextDateSelectedFrom && nextDateSelectedFrom.toMillis());
    setDateSelectedToMillis(nextDateSelectedTo && nextDateSelectedTo.toMillis());
  };

  return (
    <PlacementManager>
      <PlacementReference>
        { children }
      </PlacementReference>

      <Placement onClose={ onClose } visible={ visible }>
        { () => (
          <PlacementArea paddingVertical="x8">
            <PlacementArea direction="horizontal">
              <PlacementArea paddingHorizontal="x8">
                <DatePickerMonth
                    data-bitkit-at-id={ dataAtIds.DatePickerMonthLeft }
                    dateSelectableFrom={ dateSelectableFrom }
                    dateSelectableTo={ dateSelectableTo }
                    dateSelectedFrom={ dateSelectedFrom }
                    dateSelectedTo={ dateSelectedTo }
                    onNextMonth={ handleNextMonthStart }
                    onPreviousMonth={ handlePreviousMonthStart }
                    onSelect={ handleSelect }
                    viewDate={ viewDateStart } />
              </PlacementArea>

              { match('56rem') && (
                <PlacementArea paddingHorizontal="x8">
                  <DatePickerMonth
                      data-bitkit-at-id={ dataAtIds.DatePickerMonthRight }
                      dateSelectableFrom={ dateSelectableFrom }
                      dateSelectableTo={ dateSelectableTo }
                      dateSelectedFrom={ dateSelectedFrom }
                      dateSelectedTo={ dateSelectedTo }
                      onNextMonth={ handleNextMonthEnd }
                      onPreviousMonth={ handlePreviousMonthEnd }
                      onSelect={ handleSelect }
                      viewDate={ viewDateEnd } />
                </PlacementArea>
              ) }

            </PlacementArea>

            <Buttons
                alignChildren="end"
                margin="x2"
                paddingHorizontal="x8">
              <Button
                  level="secondary"
                  onClick={ handleClose }
                  size="small">
                Cancel
              </Button>

              <Button
                  data-bitkit-at-id={ dataAtIds.DatePickerApply }
                  level="primary"
                  onClick={ handleApply }
                  size="small">
                Apply
              </Button>
            </Buttons>
          </PlacementArea>
        ) }
      </Placement>
    </PlacementManager>
  );
};

export default DatePicker;
